import { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { MapPin, Phone, Clock, Mail, Send, Navigation2, ChevronDown, Check } from 'lucide-react';
import { useSiteSettings } from '../hooks/useContent';
import { usePageSeo } from '../hooks/usePageSeo';
import { buildOrganizationSchema, buildBreadcrumbSchema } from '../utils/seo';
import { useAuth } from '../context/AuthContext';
import { contactAPI } from '../services/api';
import { getContactOfficeAnchorId } from '../utils/contactOfficeAnchor';
import { buildTelHrefFromDisplay, parsePhoneDisplaySegments } from '../utils/resolveOfficeTelHref';
import { trackLeadSubmission } from '../utils/googleAnalytics.js';
import './Contact.css';

function ContactOfficePhones({ phonePrimary, phoneSecondary }) {
  const segments = parsePhoneDisplaySegments(phonePrimary, phoneSecondary);
  if (!segments.length) return null;
  return (
    <p className="contact-office-phones">
      {segments.map((segment, index) => (
        <span key={`${segment}-${index}`}>
          {index > 0 ? <span className="contact-office-phone-sep"> / </span> : null}
          <a href={buildTelHrefFromDisplay(segment)} className="contact-office-phone-link">
            {segment}
          </a>
        </span>
      ))}
    </p>
  );
}

const SUBJECT_OPTIONS = ['General Inquiry', 'Upcoming Event', 'Volunteering', 'Partnership'];
const GOOGLE_MAPS_SEARCH_BASE = 'https://www.google.com/maps/search/?api=1&query=';

const toGoogleMapsSearchLink = (query) => {
  const q = String(query || '').trim();
  if (!q) return '';
  return `${GOOGLE_MAPS_SEARCH_BASE}${encodeURIComponent(q)}`;
};

const getOfficeMapHref = (office) => {
  const mapLink = String(office?.mapLink || '').trim();
  if (mapLink) return mapLink;
  const query = [office?.officeName, office?.address, office?.city].filter(Boolean).join(', ');
  return toGoogleMapsSearchLink(query);
};

const Contact = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { settings } = useSiteSettings();
  const [offices, setOffices] = useState([]);
  const publicBase =
    String(settings.site_public_url || '').replace(/\/$/, '') ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const brand = settings.site_brand_name || 'Invincible NGO';

  const contactSchema = useMemo(() => {
    const org = buildOrganizationSchema(settings);
    const breadcrumbs = buildBreadcrumbSchema([
      { name: 'Contact Us', url: '/contact' }
    ], publicBase);
    
    const localBusinesses = (offices || []).map((o, idx) => ({
      '@type': 'LocalBusiness',
      '@id': `${publicBase}/contact#office-${idx}`,
      name: `${brand} - ${o.officeName || o.city || 'Office'}`,
      telephone: o.phonePrimary || o.phoneSecondary || '+919099400699',
      address: {
        '@type': 'PostalAddress',
        streetAddress: o.address || '',
        addressLocality: o.city || 'Ahmedabad',
        addressRegion: 'Gujarat',
        addressCountry: 'IN'
      }
    }));

    return {
      '@context': 'https://schema.org',
      '@graph': [org, breadcrumbs, ...localBusinesses]
    };
  }, [settings, publicBase, brand, offices]);

  usePageSeo({
    title: `${settings.contact_hero_title || 'Contact Us'} | ${brand}`,
    description:
      String(settings.contact_hero_subtitle || '').trim() ||
      settings.site_meta_description,
    canonical: publicBase ? `${publicBase}/contact` : undefined,
    schema: contactSchema
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'General Inquiry',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [subjectMenuOpen, setSubjectMenuOpen] = useState(false);
  const subjectFieldRef = useRef(null);

  useEffect(() => {
    const loadOffices = async () => {
      try {
        const response = await contactAPI.getOffices();
        setOffices(response.data?.data || []);
      } catch (error) {
        console.error('Failed to load contact offices:', error);
      }
    };
    loadOffices();
  }, []);

  useEffect(() => {
    const scrollToTarget = () => {
      const hash = (location.hash || '').replace(/^#/, '');
      if (hash && document.getElementById(hash)) {
        document.getElementById(hash).scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (location.pathname === '/contact/regional') {
        document.getElementById('contact-regional-offices')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    const t0 = window.setTimeout(scrollToTarget, 0);
    const t1 = window.setTimeout(scrollToTarget, 200);
    return () => {
      window.clearTimeout(t0);
      window.clearTimeout(t1);
    };
  }, [location.pathname, location.hash, offices]);

  useEffect(() => {
    if (!subjectMenuOpen) return;
    const onDocMouseDown = (e) => {
      if (subjectFieldRef.current && !subjectFieldRef.current.contains(e.target)) {
        setSubjectMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setSubjectMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [subjectMenuOpen]);

  useEffect(() => {
    if (!user) return;
    const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    const em = String(user.email || '').trim();
    setFormData((prev) => ({
      ...prev,
      ...(fullName ? { name: fullName } : {}),
      ...(em ? { email: em } : {})
    }));
  }, [user]);

  const accountDisplayName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
    : '';
  const accountEmail = user ? String(user.email || '').trim() : '';
  const signedInSummary = Boolean(user && accountDisplayName && accountEmail);
  const showNameInput = !user || !accountDisplayName;
  const showEmailInput = !user || !accountEmail;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    const fullName = user
      ? [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || formData.name.trim()
      : formData.name.trim();
    const emailAddr = user && String(user.email || '').trim()
      ? String(user.email || '').trim()
      : formData.email.trim();
    if (!fullName) {
      setErrorMessage('Please enter your full name.');
      setIsSubmitting(false);
      return;
    }
    if (!emailAddr) {
      setErrorMessage('Please enter your email address.');
      setIsSubmitting(false);
      return;
    }
    try {
      await contactAPI.submitInquiry({
        fullName,
        email: emailAddr,
        subject: formData.subject,
        message: formData.message
      });
      setSubmitted(true);
      trackLeadSubmission({
        formType: formData.subject || 'General Inquiry',
        email: emailAddr,
        fullName
      });
      setFormData((prev) => ({
        ...prev,
        message: '',
        subject: 'General Inquiry',
        ...(user
          ? {
              name: [user.firstName, user.lastName].filter(Boolean).join(' ').trim(),
              email: String(user.email || '').trim()
            }
          : { name: '', email: '' })
      }));
      setTimeout(() => setSubmitted(false), 3000);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Unable to send your message right now.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <header className="contact-hero">
        <h1 className="contact-hero-title">
          {settings.contact_hero_title || 'Contact'}
        </h1>
        <p className="contact-hero-subtitle">
          {settings.contact_hero_subtitle || 'Life is either a daring adventure or nothing.'}
        </p>
      </header>

      {/* Offices grid under title */}
      <section id="contact-regional-offices" className="contact-offices-grid">
        {offices.length > 0 ? (
          offices.map((office) => (
            <article key={office.id} id={getContactOfficeAnchorId(office)} className="contact-office-card">
              <h3 className="contact-office-title">{office.officeName || office.city}</h3>
              <div className="contact-office-body">
                <div className="contact-office-row contact-office-row--address">
                  <span className="contact-office-icon">
                    <MapPin size={18} />
                  </span>
                  <p>{office.address}</p>
                </div>
                {office.timings && (
                  <div className="contact-office-row">
                    <span className="contact-office-icon">
                      <Clock size={18} />
                    </span>
                    <p>{office.timings}</p>
                  </div>
                )}
                {(office.phonePrimary || office.phoneSecondary) && (
                  <div className="contact-office-row">
                    <span className="contact-office-icon">
                      <Phone size={18} />
                    </span>
                    <ContactOfficePhones
                      phonePrimary={office.phonePrimary}
                      phoneSecondary={office.phoneSecondary}
                    />
                  </div>
                )}
              </div>
              <a
                className="contact-office-cta"
                href={getOfficeMapHref(office)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Navigation2 size={16} />
                View on Map
              </a>
            </article>
          ))
        ) : (
          <>
            <article
              id={getContactOfficeAnchorId({ id: 0, city: 'Ahmedabad', officeName: 'Ahmedabad (Head Office)' })}
              className="contact-office-card"
            >
              <h3 className="contact-office-title">Ahmedabad (Head Office)</h3>
              <div className="contact-office-body">
                <div className="contact-office-row contact-office-row--address">
                  <span className="contact-office-icon">
                    <MapPin size={18} />
                  </span>
                  <p>308, University Plaza, Above Chocolate Room, Vijay Cross Roads, Navrangpura, Ahmedabad, Gujarat 380009</p>
                </div>
                <div className="contact-office-row">
                  <span className="contact-office-icon">
                    <Clock size={18} />
                  </span>
                  <p>10AM To 8PM</p>
                </div>
                <div className="contact-office-row">
                  <span className="contact-office-icon">
                    <Phone size={18} />
                  </span>
                  <ContactOfficePhones
                    phonePrimary="+91 9099 400 699"
                    phoneSecondary="+91 9913 800 699"
                  />
                </div>
              </div>
              <a
                className="contact-office-cta"
                href={toGoogleMapsSearchLink('308, University Plaza, Above Chocolate Room, Vijay Cross Roads, Navrangpura, Ahmedabad, Gujarat 380009')}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Navigation2 size={16} />
                View on Map
              </a>
            </article>
          </>
        )}
      </section>

      {/* Form full-width under grid */}
      <section className="contact-form-section">
        <div className="contact-form-card">
          <h2 className="contact-form-heading">
            <span className="contact-form-heading-icon">
              <Mail size={22} />
            </span>
            <span>Send Us A Message</span>
          </h2>
          <form onSubmit={handleSubmit} className="contact-form" noValidate>
            {signedInSummary ? (
              <p className="contact-form-signed-in" role="status">
                Signed in as <strong>{accountDisplayName}</strong>
                <span className="contact-form-signed-in-email"> · {accountEmail}</span>
                <span className="contact-form-signed-in-note">
                  {`We'll include these details with your message.`}
                </span>
              </p>
            ) : null}
            {(showNameInput || showEmailInput) && !signedInSummary ? (
              <div
                className={`contact-form-row${showNameInput && showEmailInput ? '' : ' contact-form-row--single'}`}
              >
                {showNameInput ? (
                  <div className="form-group">
                    <label htmlFor="name">Full Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                ) : null}
                {showEmailInput ? (
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="form-group contact-subject-group" ref={subjectFieldRef}>
              <label id="subject-label" htmlFor="contact-subject-trigger">
                Subject
              </label>
              <div className="contact-subject-inner">
                <button
                  type="button"
                  id="contact-subject-trigger"
                  className={`contact-subject-trigger ${subjectMenuOpen ? 'is-open' : ''}`}
                  aria-haspopup="listbox"
                  aria-expanded={subjectMenuOpen}
                  aria-labelledby="subject-label"
                  onClick={() => setSubjectMenuOpen((o) => !o)}
                >
                  <span className="contact-subject-trigger-value">{formData.subject}</span>
                  <ChevronDown size={20} className="contact-subject-chevron" aria-hidden />
                </button>
                {subjectMenuOpen && (
                  <ul
                    className="contact-subject-menu"
                    role="listbox"
                    aria-labelledby="subject-label"
                  >
                    {SUBJECT_OPTIONS.map((opt) => (
                      <li key={opt} role="presentation">
                        <button
                          type="button"
                          role="option"
                          aria-selected={formData.subject === opt}
                          className={`contact-subject-option ${formData.subject === opt ? 'is-selected' : ''}`}
                          onClick={() => {
                            setFormData({ ...formData, subject: opt });
                            setSubjectMenuOpen(false);
                          }}
                        >
                          <span className="contact-subject-option-label">{opt}</span>
                          {formData.subject === opt ? (
                            <Check size={18} className="contact-subject-option-check" aria-hidden />
                          ) : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                rows="6"
                placeholder="How can we help you?"
                value={formData.message}
                onChange={handleChange}
                required
              />
            </div>

            {submitted && (
              <div className="success-message">
                Thank you! Your message has been sent.
              </div>
            )}
            {errorMessage && (
              <div className="error-message">
                {errorMessage}
              </div>
            )}

            <button type="submit" className="contact-submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Sending…' : 'Send Message'}
              <Send size={18} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
